import React, { useState, useEffect, useCallback } from "react";
import { Info } from "lucide-react";
import api from "config/axiosConfig";
import Modal from "ui/Modal";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "ui/InputOTP";
import { useUser } from "util/UserContext";
import { REGEXP_ONLY_DIGITS } from 'input-otp';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({ isOpen, onClose, email }) => {
  const [verificationCode, setVerificationCode] = useState<string>("");
  const { refreshUser } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const handleVerify = useCallback(async () => {
    if (verificationCode.length !== 6) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await api.post("/auth/email/verify", { code: verificationCode });
      if (response.status === 200) {
        await refreshUser();
        onClose();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to verify email");
      setVerificationCode("");
    } finally {
      setIsSubmitting(false);
    }
  }, [verificationCode, onClose, refreshUser]);

  // Auto-submit when code is complete
  useEffect(() => {
    if (verificationCode.length === 6 && !isSubmitting) {
      handleVerify();
    }
  }, [verificationCode, handleVerify, isSubmitting]);

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    try {
      await api.post("/auth/passcode", { emailOrUsername: email, codeType: "email_verification" });
      setResendTimer(30);
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to resend code");
    } finally {
      setIsResending(false);
    }
  };

  const formatEmail = (email: string) => {
    const [username, domain] = email.split('@');
    if (username.length <= 3) return email;
    return `${username.slice(0, 3)}...@${domain}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        refreshUser();
        onClose();
      }}
      title="Verify your email"
      showSkip={true}
    >
        <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
            <div className="flex flex-col flex-grow space-y-4">
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                Enter the 6-digit code we sent to {formatEmail(email)}
                </p>

                <div className="flex justify-center">
                    <InputOTP
                        maxLength={6}
                        pattern={REGEXP_ONLY_DIGITS}
                        inputMode="numeric"
                        value={verificationCode}
                        autoFocus={true}
                        onChange={(value) => {
                        setError(null);
                        setVerificationCode(value);
                        }}
                        containerClassName="group flex items-center"
                        disabled={isSubmitting}
                    >
                        <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                        </InputOTPGroup>

                    </InputOTP>
                </div>

                {error && (
                <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                    <Info className="mr-1 size-4" />
                    <span>{error}</span>
                </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col justify-end place-items-center pb-8 mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-start sm:place-items-start sm:mb-0">
                    <div className="text-center sm:justify-start">
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Didn't get an email?{" "}
                            {resendTimer > 0 ? (
                            <span>
                                Resend in {Math.floor(resendTimer / 60)}:
                                {(resendTimer % 60).toString().padStart(2, "0")}
                            </span>
                            ) : (
                            <button
                                onClick={handleResend}
                                disabled={isResending}
                                className="text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline disabled:opacity-50"
                            >
                                Resend
                            </button>
                            )}
                        </span>
                    </div>
                    <button
                    type="submit"
                    onClick={handleVerify}
                    disabled={verificationCode.length !== 6 || isSubmitting}
                    className={`w-full sm:w-fit py-2 px-4 place-self-end ${
                        verificationCode.length !== 6 || isSubmitting ? 'btn-primary-disabled' : 'btn-primary'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                    {isSubmitting ? "Verifying..." : "Continue"}
                    </button>
                </div>
            </div>
        </div>
    </Modal>
  );
};

export default EmailVerificationModal;
